import { useState, useCallback, useEffect, useRef } from 'react';
import { useMentionUsers, MentionUser, MentionData } from 'src/hooks/useMentionUsers';
import { isValidEmail } from 'src/lib/validation/email';

export interface UseMentionsOptions {
  value: string;
  onChange: (value: string) => void;
  mentions: MentionData[];
  onMentionsChange: (mentions: MentionData[]) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  groupId?: string | null;
}

export function useMentions({ value, onChange, mentions, onMentionsChange, textareaRef, groupId }: UseMentionsOptions) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { suggestions, loading, search, clear } = useMentionUsers(groupId);

  const [showDropdown, setShowDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartPos, setMentionStartPos] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [emailInput, setEmailInput] = useState('');

  const resetState = useCallback(() => {
    setShowDropdown(false);
    setMentionQuery('');
    setMentionStartPos(null);
    setEmailInput('');
    clear();
  }, [clear]);

  const reconcileMentions = useCallback(
    (text: string, currentMentions: MentionData[]) => {
      const reconciled = currentMentions.filter((m) => {
        if (!m.displayText) return true;
        return text.includes(m.displayText);
      });
      if (reconciled.length !== currentMentions.length) {
        onMentionsChange(reconciled);
      }
    },
    [onMentionsChange]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      const cursorPos = e.target.selectionStart;
      onChange(newValue);

      reconcileMentions(newValue, mentions);

      const textBeforeCursor = newValue.substring(0, cursorPos);
      const atMatch = textBeforeCursor.match(/@([^\s@]*)$/);

      if (atMatch) {
        const query = atMatch[1];
        setMentionQuery(query);
        setMentionStartPos(cursorPos - query.length - 1);
        setShowDropdown(true);
        setSelectedIndex(0);
        setEmailInput(isValidEmail(query) ? query : '');

        if (query.length >= 1 && !isValidEmail(query)) {
          search(query);
        }
      } else {
        resetState();
      }
    },
    [onChange, search, resetState, reconcileMentions, mentions]
  );

  const insertMention = useCallback(
    (user: MentionUser) => {
      if (mentionStartPos === null || !textareaRef.current) return;

      const before = value.substring(0, mentionStartPos);
      const after = value.substring(mentionStartPos + mentionQuery.length + 1);
      const displayName = user.firstname && user.surname ? `${user.firstname} ${user.surname}` : user.email;
      const newValue = `${before}${displayName} ${after}`;

      onChange(newValue);
      const alreadyMentioned = mentions.some((m) => m.email?.toLowerCase() === user.email.toLowerCase());
      if (!alreadyMentioned) {
        onMentionsChange([...mentions, { user_id: user.user_id, email: user.email, displayText: displayName }]);
      }
      resetState();

      requestAnimationFrame(() => {
        if (textareaRef.current) {
          const newCursorPos = before.length + displayName.length + 1;
          textareaRef.current.selectionStart = newCursorPos;
          textareaRef.current.selectionEnd = newCursorPos;
          textareaRef.current.focus();
        }
      });
    },
    [value, mentionStartPos, mentionQuery, mentions, onChange, onMentionsChange, resetState, textareaRef]
  );

  const insertEmailMention = useCallback(
    (email: string) => {
      if (mentionStartPos === null || !textareaRef.current) return;

      const before = value.substring(0, mentionStartPos);
      const after = value.substring(mentionStartPos + mentionQuery.length + 1);
      const newValue = `${before}${email} ${after}`;

      onChange(newValue);
      const alreadyMentioned = mentions.some((m) => m.email?.toLowerCase() === email.toLowerCase());
      if (!alreadyMentioned) {
        onMentionsChange([...mentions, { email, displayText: email }]);
      }
      resetState();

      requestAnimationFrame(() => {
        if (textareaRef.current) {
          const newCursorPos = before.length + email.length + 1;
          textareaRef.current.selectionStart = newCursorPos;
          textareaRef.current.selectionEnd = newCursorPos;
          textareaRef.current.focus();
        }
      });
    },
    [value, mentionStartPos, mentionQuery, mentions, onChange, onMentionsChange, resetState, textareaRef]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!showDropdown) return;

      const totalItems = suggestions.length + (emailInput ? 1 : 0);
      if (totalItems === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % totalItems);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + totalItems) % totalItems);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        if (selectedIndex < suggestions.length) {
          insertMention(suggestions[selectedIndex]);
        } else if (emailInput) {
          insertEmailMention(emailInput);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        resetState();
      }
    },
    [showDropdown, suggestions, emailInput, selectedIndex, insertMention, insertEmailMention, resetState]
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        resetState();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [resetState]);

  return {
    handleChange,
    handleKeyDown,
    dropdownRef,
    showDropdown,
    suggestions,
    loading,
    emailInput,
    selectedIndex,
    setSelectedIndex,
    insertMention,
    insertEmailMention,
    hasResults: suggestions.length > 0 || !!emailInput
  };
}
